import React, { useCallback, useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  TextField,
  Typography,
  IconButton,
  Divider,
  InputAdornment,
  CircularProgress,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { v4 as uuid } from "uuid";
import DropdownTextField from "../form-components/DropdownTextField";
import LocalText from "../common/LocalText";
import { textFieldSx } from '../../utils/dropdownStyles';
import { hasRequiredName, isValidUrl } from '../../utils/validators';
import useUrlValidation from '../../hooks/useUrlValidation';
import UrlStatusIcon from '../common/UrlStatusIcon';
import { getSpaceId } from '../../api/client';

const SectionLabel = ({ children, required }) => (
  <Typography
    variant="caption"
    sx={{
      display: "block",
      fontWeight: 700,
      textTransform: "uppercase",
      letterSpacing: 0.8,
      color: "text.secondary",
      mb: 0.5,
    }}
  >
    <LocalText>{children}</LocalText>
    {required && <span style={{ color: '#d32f2f', marginLeft: 3 }}>*</span>}
  </Typography>
);

const CharityModal = ({
  open,
  mode = "create",
  initialData,
  onSave,
  onClose,
  loading = false,
  readOnly = false,
}) => {
  const [nameValues,  setNameValues]  = useState([{ localeId: "en_us", value: "" }]);
  const [descValues,  setDescValues]  = useState([{ localeId: "en_us", value: "" }]);
  const [mediaValues, setMediaValues] = useState([{ localeId: "en_us", mediaLink: "" }]);
  const [websiteUrl,  setWebsiteUrl]  = useState("");
  const [donationUrl, setDonationUrl] = useState("");
  const [mediaUrlsValid, setMediaUrlsValid] = useState(true);
  const [baseFields, setBaseFields] = useState({
    spaceId: 1,
    isActive: true,
    localizedNameKey: uuid(),
    localizedDescriptionKey: uuid(),
    mediaId: 0,
  });

  useEffect(() => {
    if (!open) return;
    if (initialData) {
      setBaseFields({
        spaceId: initialData.spaceId ?? getSpaceId(),
        isActive: initialData.isActive ?? true,
        localizedNameKey: initialData.localizedName?.key ?? uuid(),
        localizedDescriptionKey: initialData.localizedDescription?.key ?? uuid(),
        mediaId: initialData.media?.id ?? 0,
      });
      setNameValues(
        initialData.localizedName?.values?.length > 0
          ? initialData.localizedName.values.map((v) => ({ localeId: v.localeId, value: v.value }))
          : [{ localeId: "en_us", value: "" }]
      );
      setDescValues(
        initialData.localizedDescription?.values?.length > 0
          ? initialData.localizedDescription.values.map((v) => ({ localeId: v.localeId, value: v.value }))
          : [{ localeId: "en_us", value: "" }]
      );
      setMediaValues(
        initialData.media?.linkLocalizations?.length > 0
          ? initialData.media.linkLocalizations.map((l) => ({ localeId: l.localeId, mediaLink: l.mediaLink }))
          : [{ localeId: "en_us", mediaLink: "" }]
      );
      setWebsiteUrl(initialData.websiteUrl || "");
      setDonationUrl(initialData.donationUrl || "");
    } else {
      setNameValues([{ localeId: "en_us", value: "" }]);
      setDescValues([{ localeId: "en_us", value: "" }]);
      setMediaValues([{ localeId: "en_us", mediaLink: "" }]);
      setWebsiteUrl("");
      setDonationUrl("");
      setMediaUrlsValid(true);
      setBaseFields({
        spaceId: getSpaceId(),
        isActive: true,
        localizedNameKey: uuid(),
        localizedDescriptionKey: uuid(),
        mediaId: 0,
      });
    }
  }, [open, initialData]);

  const { status: websiteUrlStatus }  = useUrlValidation(websiteUrl,  'url');
  const { status: donationUrlStatus } = useUrlValidation(donationUrl, 'url');

  const hasMediaLink = mediaValues.some((m) => (m.mediaLink ?? '').trim().length > 0);

  const urlHelperText = (url, status, fieldLabel) => {
    if (readOnly) return '';
    if (!url.trim()) return 'Required';
    if (status === 'valid') return `${fieldLabel} is valid`;
    if (status !== 'invalid') return '';
    return isValidUrl(url) ? 'URL could not be reached' : 'URL must start with http:// or https://';
  };
  const websiteUrlHelperText = urlHelperText(websiteUrl, websiteUrlStatus, 'Website URL');
  const donationUrlHelperText = urlHelperText(donationUrl, donationUrlStatus, 'Donation URL');

  const isValid =
    hasRequiredName(nameValues) &&
    websiteUrl.trim() !== ''   && websiteUrlStatus  !== 'invalid' &&
    donationUrl.trim() !== ''  && donationUrlStatus !== 'invalid' &&
    hasMediaLink &&
    mediaUrlsValid;

  const handleMediaValidation = useCallback((allValid) => setMediaUrlsValid(allValid), []);

  const handleSave = () => {
    const payload = {
      id: initialData?.id ?? null,
      spaceId: baseFields.spaceId,
      websiteUrl,
      donationUrl,
      isActive: baseFields.isActive,
      localizedName: {
        key: baseFields.localizedNameKey,
        values: nameValues.map((v) => ({ localeId: v.localeId, value: v.value })),
      },
      localizedDescription: {
        key: baseFields.localizedDescriptionKey,
        values: descValues.map((v) => ({ localeId: v.localeId, value: v.value })),
      },
      media: {
        id: baseFields.mediaId ?? 0,
        mediaTypeId: 1,
        linkLocalizations: mediaValues.map((v) => ({ localeId: v.localeId, mediaLink: v.mediaLink })),
      },
    };
    onSave(payload);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{ sx: { borderRadius: 3, overflow: "hidden", height: "85vh" } }}
    >
      <DialogTitle sx={{ bgcolor: "primary.main", color: "white", pr: 6, py: 2.5 }}>
        <Typography variant="h6" fontWeight={700} component="span" sx={{ display: "block" }}>
          <LocalText>{readOnly ? "View Charity" : mode === "edit" ? "Edit Charity" : "Add Charity"}</LocalText>
        </Typography>
        <IconButton
          onClick={(e) => { e.currentTarget.blur(); onClose(); }}
          size="small"
          sx={{ position: "absolute", right: 10, top: 12, color: "white" }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ p: 3, overflow: "auto" }}>
        {loading ? (
          <Box display="flex" justifyContent="center" py={5}>
            <CircularProgress />
          </Box>
        ) : (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>

            <SectionLabel required>Name</SectionLabel>
            <DropdownTextField
              label="Name"
              values={nameValues}
              attribute="value"
              onChange={setNameValues}
              disabled={readOnly}
              required={!readOnly}
            />

            <SectionLabel>Description</SectionLabel>
            <DropdownTextField
              label="Description"
              values={descValues}
              attribute="value"
              onChange={setDescValues}
              disabled={readOnly}
            />

            <Divider sx={{ my: 1 }} />

            <SectionLabel required>Website URL</SectionLabel>
            <TextField
              fullWidth
              size="small"
              placeholder="https://..."
              value={websiteUrl}
              onChange={(e) => setWebsiteUrl(e.target.value)}
              error={!readOnly && websiteUrlStatus === 'invalid'}
              helperText={websiteUrlHelperText}
              disabled={readOnly}
              InputProps={readOnly ? undefined : {
                endAdornment: (
                  <InputAdornment position="end">
                    <UrlStatusIcon status={websiteUrlStatus} />
                  </InputAdornment>
                ),
              }}
              sx={{ mb: 2, ...textFieldSx }}
            />

            <SectionLabel required>Donation URL</SectionLabel>
            <TextField
              fullWidth
              size="small"
              placeholder="https://..."
              value={donationUrl}
              onChange={(e) => setDonationUrl(e.target.value)}
              error={!readOnly && donationUrlStatus === 'invalid'}
              helperText={donationUrlHelperText}
              disabled={readOnly}
              InputProps={readOnly ? undefined : {
                endAdornment: (
                  <InputAdornment position="end">
                    <UrlStatusIcon status={donationUrlStatus} />
                  </InputAdornment>
                ),
              }}
              sx={{ mb: 2, ...textFieldSx }}
            />

            <Divider sx={{ my: 1 }} />

            <SectionLabel required>Media Link (Image)</SectionLabel>
            <DropdownTextField
              label="Image URL"
              values={mediaValues}
              attribute="mediaLink"
              onChange={setMediaValues}
              previewable={!readOnly}
              mediaType="image"
              urlValidationType={readOnly ? null : "image"}
              onValidationChange={readOnly ? undefined : handleMediaValidation}
              disabled={readOnly}
              required={!readOnly}
            />

          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ justifyContent: "center", p: 3, bgcolor: "grey.50" }}>
        {readOnly ? (
          <Button onClick={onClose} sx={{ mr: 2 }}>
            <LocalText>Close</LocalText>
          </Button>
        ) : (
          <>
            <Button onClick={onClose} sx={{ mr: 2 }}>
              <LocalText>Cancel</LocalText>
            </Button>
            <Button
              variant="contained"
              onClick={handleSave}
              disabled={!isValid}
              size="large"
              sx={{ px: 6, py: 1.5, fontWeight: 700, borderRadius: 2 }}
            >
              <LocalText>{mode === "edit" ? "Update" : "Save"}</LocalText>
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default CharityModal;
